import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';

import {
  Row,
  Col,
} from '@bingads-webui-react/primitive';
import { Select } from '@bingads-webui-react/primitive-with-signal';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import ValidationLegacyStrings from '@bingads-webui-clientcenter/validation-legacy-strings';
import { Input } from '@bingads-webui-react/primitive-with-validation-and-signal';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { Constants, CountryUtils } from '@bingads-webui-clientcenter/entity-utils';
import { isControl } from '@bingads-webui-universal/primitive-utilities';

import { DefaultPhoneNumberMaxLength } from './constants';

const { PhoneNumberWithoutPrefixRegExp } = Constants;

export const selectCountryCallingCodeOptions = countries => _.map(countries, country =>
  <option key={country.Id} value={country.Id}>{CountryUtils.getCallingCodeDisplayByCountry(country)}</option>);

export class PhoneNumber extends React.Component {
  constructor(props) {
    super(props);
    const {
      initialPhoneNumber,
      initialCallingCodeId,
      initialCountryId,
    } = this.parseInitialPhoneNumber();
    this.state = {
      countryId: initialCountryId,
      phoneNumber: initialPhoneNumber,
      callingCodeId: initialCallingCodeId,
    };
  }

  componentDidMount() {
    const selectedId = this.state.countryId;
    this.onCountryChange(selectedId);
  }

  onValidate = (result) => {
    this.setState({
      phoneNumber: result.data.phoneNumber,
      phoneNumberWithCallingCode: `${this.state.callingCodeId}-${result.data.phoneNumber}`,
      isValid: result.isValid,
    }, () => this.props.onPhoneNumberChange(this.state));
  };

  onCountryChange = (selectedCountryId) => {
    const selectedCountry = _.findWhere(this.props.countries, { Id: selectedCountryId });
    const callingCodeId = _.get(selectedCountry, 'CountryCallingCode', '1');
    this.setState({
      countryId: selectedCountryId,
      callingCodeId,
      phoneNumberWithCallingCode: `${callingCodeId}-${this.state.phoneNumber}`,
    }, () => this.props.onPhoneNumberChange(this.state));
  };

  getLegacyI18n = () => buildLegacyI18n(this.props.i18n, ValidationLegacyStrings);

  parseInitialPhoneNumber = () => {
    const {
      parseCallingCode,
      phoneNumber,
      countries,
      initialCountryCode,
    } = this.props;

    if (parseCallingCode && phoneNumber && phoneNumber !== '') {
      const matches = phoneNumber.match(/^(\d+)\s?-\s?(.*$)/);
      if (matches) {
        const fromCallingCode =
          _.findWhere(countries, { CountryCallingCode: matches[1] });
        const fromCountryCode =
          _.findWhere(this.props.countries, { Code: initialCountryCode });

        let initialCountry = fromCallingCode;

        // This is a tricky fix here that Canada has the same calling code as US.
        // If it can find the country from the address, use that one first.
        if (fromCallingCode &&
          fromCountryCode &&
          fromCallingCode.CountryCallingCode === fromCountryCode.CountryCallingCode) {
          initialCountry = fromCountryCode;
        }

        if (initialCountry) {
          return {
            initialPhoneNumber: matches[2],
            initialCountryId: initialCountry.Id,
            initialCallingCodeId: initialCountry.CountryCallingCode,
          };
        }
      }
    } else if (initialCountryCode) {
      const initialCountry = _.findWhere(this.props.countries, { Code: initialCountryCode });
      if (initialCountry) {
        return {
          initialPhoneNumber: '',
          initialCountryId: initialCountry.Id,
          initialCallingCodeId: initialCountry.CountryCallingCode,
        };
      }
    }

    return {
      initialPhoneNumber: this.props.phoneNumber,
      initialCountryId: 191,
      initialCallingCodeId: '1',
    };
  };

  customValidator = (value, defaultValidator) => {
    const { i18n } = this.props;
    const validationMessages = defaultValidator(value);
    if (!_.isEmpty(validationMessages)) {
      return validationMessages;
    }
    return isControl(value) ? [i18n.getString(_TL_('Enter a valid value'))] : [];
  };

  renderDropdown = (countries, countryId, onCountryChange, key, label, isReadonly, disabled) => {
    if (this.props.showCountryCallingCode) {
      const selectedItem = _.findWhere(countries, { Id: countryId }) || _.first(countries);
      if (isReadonly) {
        return CountryUtils.getCallingCodeDisplayByCountry(selectedItem);
      }
      return (
        <Select
          value={selectedItem.Id}
          onChange={({
            target: { value: newValue },
          }) => onCountryChange(Number(newValue))}
          key={key}
          name={`phone-number-${key}`}
          aria-label={label}
          disabled={disabled}
        >
          {selectCountryCallingCodeOptions(countries)}
        </Select>);
    }
    return null;
  };

  render() {
    const {
      id,
      i18n,
      countries,
      alwaysShowError,
      isReadonly,
      isOptional,
      disabled,
    } = this.props;
    const legacyI18n = this.getLegacyI18n();

    const label = i18n.getString(_TL_('Phone number'));

    const customErrors = {
      required: i18n.getString(_TL_('Please enter a phone number')),
      minLength: i18n.getString(_TL_('Business phone must be 1 letter or more')),
      maxLength: i18n.getString(_TL_('Business phone must be 50 letters or less')),
      pattern: i18n.getString(_TL_('Please enter a valid phone number. You may use 0-9, ext, spaces, and the . () - + characters.')),
    };

    const errorProcessor = errors => _.map(
      errors,
      (oldError, key) => customErrors[key] || oldError
    );

    return (
      <LineByLineFormGroup
        id={`${id}_PhoneNumberFormGroup`}
        label={label}
        isStatic={isReadonly}
      >
        <Row>
          <Col xs={4} >
            {this.renderDropdown(countries, this.state.countryId, this.onCountryChange, 'Country', label, isReadonly, disabled)}
          </Col>
          <Col xs={8}>
            {isReadonly && this.state.phoneNumber}
            {!isReadonly && <Input
              aria-label={label}
              key="PhoneNumber"
              name="PhoneNumber"
              id={`${id}_PhoneNumber`}
              className="form-control"
              disabled={disabled}
              validationProps={{
                onValidate: this.onValidate,
                field: 'phoneNumber',
                data: { phoneNumber: this.state.phoneNumber },
                schema: {
                  properties: {
                    phoneNumber: {
                      type: 'string',
                      required: !isOptional,
                      maxLength: DefaultPhoneNumberMaxLength,
                      pattern: PhoneNumberWithoutPrefixRegExp,
                    },
                  },
                },
                validator: this.customValidator,
                alwaysShowError,
                errorProcessor,
                i18n: legacyI18n,
              }}
            />}
          </Col>
        </Row>
      </LineByLineFormGroup>
    );
  }
}

PhoneNumber.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  isReadonly: PropTypes.bool,
  onPhoneNumberChange: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  alwaysShowError: PropTypes.bool,
  initialCountryCode: PropTypes.string,
  phoneNumber: PropTypes.string,
  parseCallingCode: PropTypes.bool,
  showCountryCallingCode: PropTypes.bool,
  isOptional: PropTypes.bool,
  disabled: PropTypes.bool,
};

PhoneNumber.defaultProps = {
  isReadonly: false,
  alwaysShowError: false,
  initialCountryCode: '',
  phoneNumber: '',
  parseCallingCode: false,
  showCountryCallingCode: false,
  isOptional: false,
  disabled: false,
};
